/**
 * The one place that maps a question's `kind` to its control.
 *
 * Every other component takes only what it needs, so this switch is the only thing that has
 * to know the full set — and the only thing to change when the canvas grows a kind. It mirrors
 * `questionKindOf` in `projection.ts`, which is the canvas's own derivation:
 *
 *     uiComponent present       -> COMPONENT   (the component name decides the whole control)
 *     LIST_SELECTION + multi    -> MULTI_SELECT
 *     LIST_SELECTION            -> SINGLE_SELECT
 *     INPUT_NUMBER              -> NUMBER
 *     otherwise                 -> TEXT
 *
 * Note the precedence: a named component WINS over the question type. That is deliberate in
 * the canvas and must stay true here, or `Q0.1` (an INPUT_TEXT carrying the SFDC component)
 * would render as a bare text box.
 */
import type { DocQuestion } from '../../utils/wire';
import type { AnswerControlProps } from '../answerTypes';
import MultiSelectAnswer from './MultiSelectAnswer';
import NumberAnswer from './NumberAnswer';
import SfdcAccountAnswer from './SfdcAccountAnswer';
import SingleSelectAnswer from './SingleSelectAnswer';
import TextAnswer from './TextAnswer';

export interface AnswerBodyProps extends AnswerControlProps {
  question: DocQuestion;
}

/**
 * Components a `COMPONENT` question may name. Mirrors `UI_COMPONENTS`.
 *
 * Each takes only the answer props. A component that needs data fetches it itself -- the SFDC
 * one calls GCRCompanyService for the caller's accounts -- because threading that through here
 * would make this switch know about every service any component might use, and `AnswerBody`
 * would gain a prop per component.
 */
const COMPONENTS: Record<string, (p: AnswerControlProps) => JSX.Element> = {
  'sfdc-account': (p) => <SfdcAccountAnswer {...p} />,
};

export default function AnswerBody(props: AnswerBodyProps) {
  const { question, ...control } = props;

  if (question.kind === 'COMPONENT') {
    const Component = question.component ? COMPONENTS[question.component] : undefined;
    if (!Component) {
      /*
       * Named and unknown. The old BD frontend rendered the literal string
       * "Custom component 'X' not found" into the page, so a typo degraded into a broken
       * question that looked like content. Saying it is a configuration problem at least
       * points at the cause.
       */
      return (
        <div className="cpa-a__missing">
          这道题指定了组件 <code>{question.component ?? '(空)'}</code>，但前端没有注册它。
          请在管理端改用注册表里的组件。
        </div>
      );
    }
    return <Component {...control} />;
  }

  switch (question.kind) {
    case 'SINGLE_SELECT':
      return <SingleSelectAnswer options={question.options ?? []} {...control} />;
    case 'MULTI_SELECT':
      return <MultiSelectAnswer options={question.options ?? []} {...control} />;
    case 'NUMBER':
      return <NumberAnswer format={question.numberFormat} {...control} />;
    case 'TEXT':
      return <TextAnswer {...control} />;
  }
}
